/* Folds the secondary navigation bars into Menu Alpha's hamburger panel below
   750px, so a phone shows one bar instead of up to four.

   It MOVES the live lists rather than cloning them. Cloning would duplicate
   every id (EscMenuBetaList-*, Details-EscMenuBeta-*), break the aria-controls
   wiring, and leave two copies to drift apart; moving keeps one source of
   truth, and because they are the same nodes, the listeners Menu Beta and the
   Machine Menu bound to their own <details> ride along and keep working.

   A comment node is left where each list was, so it goes back exactly where it
   came from when the viewport grows. */

const ESC_MOBILE_NAV_QUERY = '(max-width: 749px)';

/* Each source names the element that owns the list, the list to move, and
   where to get the heading text. Beta and the Machine Menu already carry a
   mobile label on their collapse toggle; the Support Menu has no toggle
   (nothing to collapse — it has no dropdowns), so it needs a fallback. */
const ESC_MOBILE_NAV_SOURCES = [
  {
    host: 'esc-menu-beta',
    list: '.esc-menu-beta__list',
    label: '.esc-menu-beta__collapse-toggle',
    fallback: 'Browse',
  },
  {
    host: 'esc-machine-menu',
    list: '.esc-machine-menu__list',
    label: '.esc-machine-menu__collapse-toggle',
    fallback: 'Machines',
  },
  {
    host: '.esc-support-menu',
    list: '.esc-support-menu__list',
    label: null,
    fallback: 'Locations',
  },
];

class EscMobileNav {
  constructor() {
    this.media = window.matchMedia(ESC_MOBILE_NAV_QUERY);
    this.moved = [];
    this.sync = this.sync.bind(this);

    this.media.addEventListener('change', this.sync);

    /* The theme editor re-renders a whole section on every settings change.
       Unload fires while the section is still in the DOM but about to go, so
       this is the last chance to hand the borrowed lists back — if Menu Alpha
       were torn down with a list still inside its panel, that list would be
       destroyed and its own bar would come back empty until a full reload.
       Unload therefore ONLY unfolds; re-folding is left to the load event that
       follows, which runs against the rebuilt DOM. */
    document.addEventListener('shopify:section:unload', () => this.unfold());
    document.addEventListener('shopify:section:load', this.sync);

    this.sync();
  }

  get slot() {
    return document.querySelector('[data-esc-mobile-nav-slot]');
  }

  sync() {
    /* Always unfold first. Re-reading the DOM from a known-flat state is far
       cheaper to reason about than diffing what is already where. */
    this.unfold();
    if (this.media.matches) this.fold();
  }

  fold() {
    const slot = this.slot;
    if (!slot) return;

    ESC_MOBILE_NAV_SOURCES.forEach((source) => {
      const host = document.querySelector(source.host);
      if (!host) return;

      const list = host.querySelector(source.list);
      if (!list || !list.children.length) return;

      const marker = document.createComment('esc-mobile-nav');
      list.parentNode.insertBefore(marker, list);

      const group = document.createElement('div');
      group.className = 'esc-menu-alpha__group';

      const heading = document.createElement('p');
      heading.className = 'esc-menu-alpha__group-label';
      heading.textContent = this.labelFor(host, source);
      group.appendChild(heading);
      group.appendChild(list);
      slot.appendChild(group);

      /* Hides the now-empty bar. The section wrapper is the target rather than
         the nav itself so the bar's padding and background go with it. */
      const section = host.closest('.shopify-section') || host;
      section.classList.add('esc-nav-merged');

      this.moved.push({ list, marker, group, section });
    });
  }

  unfold() {
    this.moved.forEach(({ list, marker, group, section }) => {
      if (marker.parentNode) {
        marker.parentNode.insertBefore(list, marker);
        marker.remove();
      }
      group.remove();
      section.classList.remove('esc-nav-merged');
    });
    this.moved = [];
  }

  /* The toggle carries an SVG caret as well as its text, so read the text
     nodes only rather than textContent. */
  labelFor(host, source) {
    if (!source.label) return source.fallback;
    const toggle = host.querySelector(source.label);
    if (!toggle) return source.fallback;
    const text = Array.from(toggle.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent.trim())
      .join(' ')
      .trim();
    return text || source.fallback;
  }
}

/* Menu Beta and the Machine Menu load their own scripts from their own
   sections, which sit AFTER Menu Alpha in the document — so this file runs
   first. It must not fold anything before those elements have upgraded: their
   connectedCallback captures `this.groups` by querying their own subtree, and
   a list already moved into Alpha's panel would leave them with no groups at
   all, killing every dropdown.

   Waiting for DOMContentLoaded is what guarantees the order, and the check has
   to be written carefully: a deferred script executes while readyState is
   already 'interactive', not 'loading', so testing for 'loading' alone would
   fall through to the immediate branch and fold too early. Only 'complete' is
   proof that DOMContentLoaded has been and gone; the load listener is the
   backstop for a script injected in the gap between the two events. */
function escMobileNavBoot() {
  if (window.escMobileNav) {
    window.escMobileNav.sync();
    return;
  }
  window.escMobileNav = new EscMobileNav();
}

if (document.readyState === 'complete') {
  escMobileNavBoot();
} else {
  document.addEventListener('DOMContentLoaded', escMobileNavBoot, { once: true });
  window.addEventListener('load', escMobileNavBoot, { once: true });
}
